import React, { useState } from 'react';
import { X, Receipt, DollarSign, Calendar, MapPin, User, FileText } from 'lucide-react';
import { Town } from '../../types';
import { darkModeClasses } from '../../utils/darkMode';
import { HelpButton } from '../Help/HelpButton';

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (expenseData: any) => void;
  towns: Town[];
  selectedTownId: string;
}

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  towns,
  selectedTownId
}) => {
  const [formData, setFormData] = useState({
    amount: '',
    category: 'Utilities',
    reason: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    townId: selectedTownId === 'all' ? towns[0]?.id || '' : selectedTownId,
    beneficiary: '',
    reference: ''
  });

  const handleCategoryChange = (category: string) => {
    setFormData({
      ...formData,
      category,
      reason: '' // Reset reason when category changes
    });
  };

  const [errors, setErrors] = useState<Record<string, string>>({});

  const expenseCategories = {
    'Utilities': ['Electricity Bills', 'Water Bills', 'Internet/Phone', 'Maintenance', 'Cleaning Supplies'],
    'Events': ['Funeral Support', 'Wedding Support', 'Birthday Celebrations', 'Festival Expenses', 'Meeting Refreshments'],
    'Welfare': ['Medical Assistance', 'Educational Support', 'Emergency Aid', 'Elderly Care', 'Child Support'],
    'Administration': ['Office Supplies', 'Documentation', 'Transportation', 'Communication', 'Banking Fees'],
    'Development': ['Infrastructure', 'Equipment Purchase', 'Training Programs', 'Community Projects', 'Technology'],
    'Emergency': ['Natural Disaster Relief', 'Urgent Medical', 'Family Crisis Support', 'Unexpected Repairs', 'Security Issues']
  };

  const getCategoryReasons = (category: string) => {
    return expenseCategories[category as keyof typeof expenseCategories] || [];
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!formData.reason) {
      newErrors.reason = 'Please select a reason';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData.townId) {
      newErrors.townId = 'Please select a town';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const expenseData = {
      ...formData,
      id: Date.now().toString(),
      amount: parseFloat(formData.amount)
    };
    
    onSave(expenseData);
    onClose();
    setFormData({
      amount: '',
      category: 'Utilities',
      reason: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      townId: selectedTownId === 'all' ? towns[0]?.id || '' : selectedTownId,
      beneficiary: '',
      reference: ''
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
            <Receipt className="h-6 w-6 mr-2 text-red-600" />
            Record Expense
          </h2>
          <div className="flex items-center space-x-2">
            <HelpButton guideId="expenses/add-expense" variant="icon" size="sm" />
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Amount (GHS) *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className={`w-full bg-white dark:bg-dark-bg-secondary border text-gray-900 dark:text-dark-text-primary rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    errors.amount ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className={`w-full bg-white dark:bg-dark-bg-secondary border text-gray-900 dark:text-dark-text-primary rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                    errors.date ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
                  }`}
                />
              </div>
              {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent`}
              >
                {Object.keys(expenseCategories).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Reason *
              </label>
              <select
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.reason ? 'border-red-500' : ''
                }`}
              >
                <option value="">Select Reason</option>
                {getCategoryReasons(formData.category).map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
              {errors.reason && <p className="text-red-500 text-sm mt-1">{errors.reason}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Town *
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={formData.townId}
                onChange={(e) => setFormData({...formData, townId: e.target.value})}
                className={`w-full bg-white dark:bg-dark-bg-secondary border text-gray-900 dark:text-dark-text-primary rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.townId ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
                }`}
              >
                <option value="">Select Town</option>
                {towns.map(town => (
                  <option key={town.id} value={town.id}>{town.name}</option>
                ))}
              </select>
            </div>
            {errors.townId && <p className="text-red-500 text-sm mt-1">{errors.townId}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Description *
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className={`w-full bg-white dark:bg-dark-bg-secondary border text-gray-900 dark:text-dark-text-primary rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none ${
                  errors.description ? 'border-red-500' : 'border-gray-300 dark:border-dark-border-primary'
                }`}
                placeholder="Describe the expense purpose"
              />
            </div>
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Beneficiary (Optional)
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.beneficiary}
                onChange={(e) => setFormData({...formData, beneficiary: e.target.value})}
                className="w-full bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border-primary text-gray-900 dark:text-dark-text-primary rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Person or organization"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Reference (Optional)
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => setFormData({...formData, reference: e.target.value})}
              className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent`}
              placeholder="Receipt number, invoice ID, etc."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Receipt className="h-4 w-4" />
              <span>Record Expense</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};